import React from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, InputAdornment, MenuItem, Snackbar, Alert,
  Grid, Typography, Divider, Avatar, Paper, Tooltip, Menu, Fade,
  Chip, CircularProgress, Select, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import WorkIcon from '@mui/icons-material/Work';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UndoIcon from '@mui/icons-material/Undo';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import UserContactDialog, { EmailStatusChip, isNotOnboarded, emailStatusOf } from './UserContactDialog';
import { useSpinner } from '../../../Widgets/Spinner';
import './ViewBox1.css';
import { WebService } from '../../../service/webService';
import { userService } from '../Scripts/UserService';
import { Prevelage } from '../Scripts/rolePrivelageCheck';
import Cookies from 'js-cookie';

const ROLE_OPTIONS = [ 'admin', 'subadmin', 'faculty' ];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
function resolveRole(user) {
  // prefer explicit role field first
  if (user?.role) return user.role.toLowerCase();
  // fallback to Fac claim
  if (user?.Fac === 1 || user?.Fac === '1') return 'faculty';
  if (user?.Fac === 0 || user?.Fac === '0') return 'student';
  return 'unknown';
}

function initials(name = '') {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[ 0 ][ 0 ].toUpperCase();
  return (parts[ 0 ][ 0 ] + parts[ 1 ][ 0 ]).toUpperCase();
}

const isOnboarded = (user) => user?.uid && user.uid !== 'N/A';

// ---------------------------------------------------------------------------
// PLACEMENT ACCESS CELL
// ---------------------------------------------------------------------------
function PlacementAccessCell({ user, onStatusChange, onNotOnboarded }) {
  const [ anchorEl, setAnchorEl ] = React.useState(null);
  const [ loading, setLoading ] = React.useState(false);
  const [ revoked, setRevoked ] = React.useState(user.placementRevoked === true);

  const webservice = React.useRef(new WebService());

  const handleOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const handleClose = () => setAnchorEl(null);

  const performAction = async (revokeValue) => {
    handleClose();

    if (!isOnboarded(user)) {
      onNotOnboarded?.(user.name);
      return;
    }

    setLoading(true);
    try {
      const res = await webservice.current.revokeJobAccess(user.uid, revokeValue);
      if (res?.success) {
        setRevoked(revokeValue);
        onStatusChange(user.uid, revokeValue);
      }
    } catch (e) {
      console.error('revokeJobAccess error:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Status chip */}
      <Chip
        size="small"
        icon={revoked
          ? <WorkOffIcon sx={{ fontSize: '13px !important' }} />
          : <WorkIcon sx={{ fontSize: '13px !important' }} />
        }
        label={revoked ? 'Revoked' : 'Active'}
        sx={{
          height: 24,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: 0.4,
          bgcolor: revoked ? '#ffeaea' : '#eafff0',
          color: revoked ? '#b82b2b' : '#1a7a3a',
          border: revoked ? '1px solid #f5c6c6' : '1px solid #b6e8c4',
          '& .MuiChip-icon': { color: revoked ? '#b82b2b' : '#1a7a3a' },
        }}
      />

      {/* Action button */}
      {!isOnboarded(user) ? (
        <Tooltip title="User not onboarded yet" placement="top">
          <span>
            <IconButton size="small" disabled sx={{ p: 0.4 }}>
              <MoreVertIcon sx={{ fontSize: 18, color: '#ccc' }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : loading ? (
        <CircularProgress size={16} sx={{ color: '#4d3e34' }} />
      ) : (
        <Tooltip title="Placement access options" placement="top">
          <IconButton size="small" onClick={handleOpen} sx={{ p: 0.4 }}>
            <MoreVertIcon sx={{ fontSize: 18, color: '#4d3e34' }} />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            border: '1px solid rgba(77,62,52,0.15)',
            minWidth: 180,
            bgcolor: '#fffef7',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="caption" sx={{ color: '#4d3e34', fontWeight: 700, letterSpacing: 0.4 }}>
            PLACEMENT ACCESS
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.68rem' }}>
            {user.name}
          </Typography>
        </Box>

        {!revoked ? (
          <MenuItem
            onClick={() => performAction(true)}
            sx={{ gap: 1.5, py: 1.2, color: '#b82b2b', fontWeight: 600, fontSize: '0.85rem', '&:hover': { bgcolor: '#fff0f0' } }}
          >
            <WorkOffIcon fontSize="small" />
            Revoke Access
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => performAction(false)}
            sx={{ gap: 1.5, py: 1.2, color: '#1a7a3a', fontWeight: 600, fontSize: '0.85rem', '&:hover': { bgcolor: '#f0fff4' } }}
          >
            <WorkIcon fontSize="small" />
            Restore Access
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ROW ACTIONS — one fixed-size slot per action so icons line up across rows
// ---------------------------------------------------------------------------
const ACTION_SIZE = 34;
const ACTION_TONES = {
  neutral: { color: '#6b5a4b', hoverColor: '#4d3e34', hoverBg: 'rgba(77,62,52,0.09)' },
  accent: { color: '#6b5a4b', hoverColor: '#4d3e34', hoverBg: 'rgba(255,222,89,0.45)' },
  danger: { color: '#c0392b', hoverColor: '#a52a1d', hoverBg: 'rgba(184,43,43,0.10)' },
};

function RowAction({ title, onClick, icon, tone = 'neutral' }) {
  const t = ACTION_TONES[ tone ];
  return (
    <Tooltip title={title} placement="top" arrow enterDelay={250}>
      <IconButton
        aria-label={title}
        onClick={onClick}
        sx={{ width: ACTION_SIZE, height: ACTION_SIZE, borderRadius: 2, color: t.color, transition: 'background-color .15s, color .15s', '&:hover': { bgcolor: t.hoverBg, color: t.hoverColor } }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

// keeps the grid aligned when a row has no such action (e.g. no "edit contact" for a signed-up user)
const EmptySlot = () => <Box sx={{ width: ACTION_SIZE, height: ACTION_SIZE, flexShrink: 0 }} />;

const ROLE_CHIP = {
  admin: { bg: '#ffe082', text: '#4d3e34' },
  subadmin: { bg: '#fff59d', text: '#4d3e34' },
  faculty: { bg: '#c8e6c9', text: '#2e7d32' },
  student: { bg: '#bbdefb', text: '#0d47a1' },
  librarian: { bg: '#e1bee7', text: '#6a1b9a' },
  superadmin: { bg: '#ffcc80', text: '#4e342e' },
};

const PAGE_SIZE_OPTIONS = [ 10, 25, 50 ]; // the server caps one page at 50

// which list each tab shows, for the embedded search (ID / name / email within THAT list)
// the search toggle: what the box searches (matches start from the beginning of the field)
const SEARCH_FIELDS = [
  { value: 'name', label: 'Name', placeholder: 'Search by name (min 3 chars)', min: 3 },
  { value: 'id', label: 'ID', placeholder: 'Search by ID', min: 1 },
  { value: 'email', label: 'Email', placeholder: 'Search by email (min 3 chars)', min: 3 },
];
const TAB_SEARCH_SCOPE = { 1: 'teachers', 5: 'students', 9: 'librarian', 12: 'admin', 15: 'subadmin', 16: 'superadmin' };

// Pager for tabs where the server cannot give a total (Admins / Sub admins / Super admin): rows-per-page,
// the visible range ("11–20", never an invented "of N") and previous / next. Next is enabled exactly while
// the server still hands out a cursor.
function RangePager({ pageIndex, pageSize, rowsOnPage, total, hasNext, onPage, onSize }) {
  const from = rowsOnPage ? pageIndex * pageSize + 1 : 0;
  const to = pageIndex * pageSize + rowsOnPage;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2.5, px: 2, py: 1, bgcolor: '#fffdf5', borderTop: '1px solid rgba(77,62,52,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#6b5a4b' }}>Rows per page</Typography>
        <Select
          variant="standard" disableUnderline value={pageSize}
          onChange={(e) => onSize(+e.target.value)}
          inputProps={{ 'aria-label': 'Rows per page' }}
          MenuProps={{ disableScrollLock: true }}
          sx={{ fontSize: '0.875rem' }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Typography variant="body2" sx={{ color: '#4d3e34', fontWeight: 600, minWidth: 96, textAlign: 'center' }}>
        {rowsOnPage ? `${from}–${to}${total > 0 ? ` of ${total}` : ''}` : '0'}
      </Typography>
      <Box>
        <IconButton aria-label="Go to previous page" disabled={pageIndex === 0} onClick={() => onPage(pageIndex - 1)}><ChevronLeftIcon /></IconButton>
        <IconButton aria-label="Go to next page" disabled={!hasNext} onClick={() => onPage(pageIndex + 1)}><ChevronRightIcon /></IconButton>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
function TableViewBox({
  data: initialData,
  navigationIndex,
  heading,
  showActions = true,
  showEdit = true,
  showDelete = true,
  showView = true,
}) {
  const { setLoading } = useSpinner();
  const webservice = React.useRef(null);
  const roleService = React.useRef(null);

  const [ pagination, setPagination ] = React.useState({ pageIndex: 0, pageSize: 10 });
  const [ globalFilter, setGlobalFilter ] = React.useState('');
  const [ searchInput, setSearchInput ] = React.useState('');
  const [ searchField, setSearchField ] = React.useState('name');
  const [ searchState, setSearchState ] = React.useState(null); // null = normal paged list; else { term, truncated }
  const [ error, setError ] = React.useState('');
  const [ data, setData ] = React.useState(initialData || []);
  const [ nextPageValue, setNextPageValue ] = React.useState('');
  const [ dataFull, setDataFullBool ] = React.useState(false);
  const [ totalEntities, setTotalEntities ] = React.useState(0);
  // pageCursors.current[i] = cursor used to load page i (page 0 = null), so "previous" can re-fetch
  const pageCursors = React.useRef([ null ]);
  // The table can keep calling the first render's page-change handler, so the handler reads the CURRENT values from here
  const live = React.useRef({});

  const [ deleteDialog, setDeleteDialog ] = React.useState({ open: false, user: null });
  const [ editDialog, setEditDialog ] = React.useState({ open: false, user: null, role: '' });
  const [ viewDialog, setViewDialog ] = React.useState({ open: false, user: null });
  const [ contactDialog, setContactDialog ] = React.useState({ open: false, user: null });

  const [ alumniDialog, setAlumniDialog ] = React.useState({ open: false, user: null });
  const [ alumniEnabled, setAlumniEnabled ] = React.useState(true);
  const [ alumniConfirmText, setAlumniConfirmText ] = React.useState('');

  // ── TOAST WITH UNDO ──────────────────────────────────────────────────────
  const [ toast, setToast ] = React.useState({ open: false, message: '', severity: 'info', undoAction: null, undoLabel: '' });
  const undoTimerRef = React.useRef(null);

  const showToast = (message, severity = 'info', undoAction = null, undoLabel = 'Undo') => {
    clearTimeout(undoTimerRef.current);
    setToast({ open: true, message, severity, undoAction, undoLabel });
    if (undoAction) {
      undoTimerRef.current = setTimeout(() => {
        setToast(t => ({ ...t, undoAction: null }));
      }, 5000);
    }
  };

  const handleUndo = async () => {
    clearTimeout(undoTimerRef.current);
    if (toast.undoAction) await toast.undoAction();
    setToast(t => ({ ...t, open: false, undoAction: null }));
  };

  React.useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  // ── HEADING MAPPING ──────────────────────────────────────────────────────
  if (navigationIndex === 1) heading = 'Teachers';
  else if (navigationIndex === 5) heading = 'Students';
  else if (navigationIndex === 9) heading = 'Libraries';
  else if (navigationIndex === 12) heading = 'Admins';

  // ── DATA FETCH ───────────────────────────────────────────────────────────
  async function fetchData({ searchEntities = '', reset = false, sortDate = false, cursor, pageSize } = {}) {
    let searchEntityType = 'teacher';
    if (navigationIndex === 5) searchEntityType = 'student';
    else if (navigationIndex === 9) searchEntityType = 'librarian';
    else if (navigationIndex === 12) searchEntityType = 'admin';
    else if (navigationIndex === 15) searchEntityType = 'subadmin';
    else if (navigationIndex === 16) searchEntityType = 'superadmin';

    try {
      setLoading(true);
      const payload = {
        query_entity_type: searchEntityType === 'teacher' ? 'Teachers' : 'Students',
        entity_search: searchEntities,
        last_pagination_value: cursor !== undefined ? cursor : (reset ? null : nextPageValue),
        page_size: pageSize ?? pagination.pageSize,
        sort_by_date: false,
      };
      if ([ 9, 12, 15, 16 ].includes(navigationIndex)) {
        payload.role = searchEntityType;
        payload.query_entity_type = 'Teachers';
      }

      const response = await webservice.current.fetchEntities(payload);
      if (!response?.success) throw new Error('Fetch failed');

      const newData = response.data.results || [];
      const newToken = response.data.nextPageToken || null;

      setData(newData);
      setNextPageValue(newToken);
      setDataFullBool(!newToken);
      if (response?.data.totalUsers) {
        setTotalEntities(response.data.totalUsers.toString());
      }
    } catch (e) {
      console.error('Fetch error:', e);
      showToast('Failed to sync with database', 'error');
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    const currentRole = Cookies.get('role');
    webservice.current = new WebService();
    roleService.current = new Prevelage(currentRole);
    fetchData();
  }, []);

  // ── PAGINATION ───────────────────────────────────────────────────────────
  live.current = { pagination, nextPageValue, dataFull, globalFilter };
  const handlePaginationChange = (updater) => {
    const { pagination, nextPageValue, dataFull, globalFilter } = live.current;
    const nextPagination = typeof updater === 'function' ? updater(pagination) : updater;
    if (nextPagination.pageSize !== pagination.pageSize) {
      // saved cursors only line up with the old page size — start again from page 1
      pageCursors.current = [ null ];
      setNextPageValue(null);
      setPagination({ pageIndex: 0, pageSize: nextPagination.pageSize });
      fetchData({ searchEntities: globalFilter, cursor: null, pageSize: nextPagination.pageSize, sortDate: !globalFilter });
      return;
    }
    const target = nextPagination.pageIndex;
    if (target > pagination.pageIndex) {
      if (dataFull) return;
      // cursor that loads page `target` = the token returned with the current page
      pageCursors.current[ target ] = nextPageValue;
      fetchData({ searchEntities: globalFilter, cursor: nextPageValue, pageSize: pagination.pageSize, sortDate: !globalFilter });
    } else if (target < pagination.pageIndex) {
      // going back: re-fetch with the cursor remembered for that page (null for page 0)
      const cursor = target === 0 ? null : (pageCursors.current[ target ] ?? null);
      fetchData({ searchEntities: globalFilter, cursor, pageSize: pagination.pageSize, sortDate: !globalFilter });
    }
    setPagination(nextPagination);
  };

  // Search by ID, name OR email, within this tab's own list. The best 50 matches replace the paged list until cleared.
  const handleClearSearch = () => {
    setSearchInput('');
    setError('');
    setSearchState(null);
    setPagination((p) => ({ pageIndex: 0, pageSize: p.pageSize }));
    pageCursors.current = [ null ];
    setNextPageValue(null);
    fetchData({ reset: true });
  };

  const handleSearchClick = async (fieldOverride) => {
    const field = typeof fieldOverride === 'string' ? fieldOverride : searchField;
    const fieldDef = SEARCH_FIELDS.find((f) => f.value === field);
    const term = searchInput.trim();
    if (term.length === 0) { if (searchState) handleClearSearch(); return; }
    if (term.length < fieldDef.min) { setError(`Please enter at least ${fieldDef.min} character${fieldDef.min === 1 ? '' : 's'}.`); return; }
    setError('');
    try {
      setLoading(true);
      const scope = TAB_SEARCH_SCOPE[ navigationIndex ] || 'all';
      const { users: found, truncated } = await userService.searchUsers(term, scope, 50, field);
      // Belt and braces: show only people who belong on THIS tab and matched on the chosen field, whatever the server
      // returned (an older deployed server ignores the tab scope and the field).
      const users = found.filter((u) => {
        const onThisTab = scope === 'teachers' ? u.designation === 'Teachers'
          : scope === 'students' ? u.designation === 'Students'
            : scope === 'all' ? true : u.role === scope;
        return onThisTab && (!Array.isArray(u.matchedOn) || u.matchedOn.includes(field));
      });
      setData(users);
      setSearchState({ term, truncated, field });
    } catch (e) {
      console.error('Search error:', e);
      showToast(e.message || 'Search failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── DELETE ───────────────────────────────────────────────────────────────
  const handleDeleteClick = (user) => {
    if (navigationIndex === 5) {
      // student — ask about alumni first
      setAlumniEnabled(true);
      setAlumniConfirmText('');
      setAlumniDialog({ open: true, user });
    } else {
      setDeleteDialog({ open: true, user });
    }
  };

  const handleAlumniDialogConfirm = () => {
    const user = alumniDialog.user;
    setAlumniDialog({ open: false, user: null });
    setDeleteDialog({ open: true, user, addToAlumni: alumniEnabled });
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const designation = navigationIndex === 5 ? '0' : '1';
      const user = deleteDialog.user;
      if (!user || !user.employee_id) throw new Error("Invalid user object — missing 'id'");
      const payload = [ { ...user, id: user.employee_id } ];
      const response = await webservice.current.deleteUser(
        payload,
        designation,
        deleteDialog.addToAlumni ?? false   // ← pass it here
      );
      if (response.success) {
        setData(prev => prev.filter(u => u.employee_id !== user.employee_id));
        setTotalEntities(prev => Math.max(0, prev - 1));
        showToast(`Deleted ${user.name || user.employee_id} successfully.`, 'success');
      } else {
        throw new Error(response.message || response.error || 'Deletion failed');
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      showToast(`Failed to delete ${deleteDialog.user?.name || 'user'}: ${error.message}`, 'error');
    } finally {
      setDeleteDialog({ open: false, user: null });
      setLoading(false);
    }
  };
  // ── EDIT ROLE ────────────────────────────────────────────────────────────
  const handleEditClick = (user) => setEditDialog({ open: true, user, role: resolveRole(user) });

  const handleRoleUpdate = async () => {
    const user = editDialog.user;
    const oldRole = resolveRole(user);
    const newRole = editDialog.role;
    const f_id = user.uid;
    const e_id = user.employee_id;

    if (!isOnboarded(user)) {
      showToast(`User: ${user.name} has not signed up yet!`, 'warning');
      setEditDialog({ open: false, user: null, role: '' });
      return;
    }

    setLoading(true);
    try {
      let response = { success: true };
      if (newRole === 'faculty') {
        if (oldRole === 'admin') response = await webservice.current.revokeAdmin(f_id, e_id);
        else if (oldRole === 'subadmin') response = await webservice.current.revokeSubAdmin(f_id, e_id);
      } else if (newRole === 'admin') {
        response = await webservice.current.createAdmin(f_id, e_id);
      } else if (newRole === 'subadmin') {
        response = await webservice.current.createSubAdmin(f_id, e_id);
      }

      if (response?.success) {
        setData(prev => prev.map(u => u.employee_id === user.employee_id ? { ...u, role: newRole } : u));
        showToast(`${user.name}'s role updated from ${oldRole} → ${newRole}`, 'success');
      } else {
        showToast(`Failed to update role for ${user.name}`, 'error');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      showToast(`Error: ${error.message || 'Role update failed'}`, 'error');
    } finally {
      setLoading(false);
      setEditDialog({ open: false, user: null, role: '' });
    }
  };

  const handleViewClick = (user) => setViewDialog({ open: true, user });

  // ── CONTACT EDIT (email / id of a user who has not signed up yet) ─────────
  const contactDesignation = navigationIndex === 5 ? 'Students' : 'Teachers';
  const handleContactClick = (user) => setContactDialog({ open: true, user: { ...user, designation: contactDesignation } });
  const handleContactUpdated = (patch) => {
    const oldId = contactDialog.user?.employee_id;
    setData(prev => prev.map(u => u.employee_id === oldId ? { ...u, ...patch } : u));
  };

  // ── PLACEMENT REVOKE WITH UNDO ────────────────────────────────────────────
  const handlePlacementStatusChange = (uid, revokedValue) => {
    setData(prev => prev.map(u => u.uid === uid ? { ...u, placementRevoked: revokedValue } : u));

    const action = revokedValue ? 'revoked' : 'restored';
    const undoValue = !revokedValue;
    const userName = data.find(u => u.uid === uid)?.name || uid;

    showToast(
      `Placement access ${action} for ${userName}`,
      revokedValue ? 'warning' : 'success',
      async () => {
        try {
          const res = await webservice.current.revokeJobAccess(uid, undoValue);
          if (res?.success) {
            setData(prev => prev.map(u => u.uid === uid ? { ...u, placementRevoked: undoValue } : u));
            showToast(`Undone — access ${undoValue ? 'revoked' : 'restored'} for ${userName}`, 'info');
          }
        } catch (e) {
          console.error('Undo error:', e);
        }
      },
      'Undo'
    );
  };

  // ── COLUMNS ──────────────────────────────────────────────────────────────
  const columns = [
    {
      header: 'Name',
      accessorKey: 'name',
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', py: 0.5 }}
          onClick={() => handleViewClick(row.original)}>
          {row.original.profile_pic ? (
            <Avatar src={row.original.profile_pic} sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar sx={{ width: 36, height: 36, backgroundColor: '#4d3e34', color: '#ffde59', fontSize: 14, fontWeight: 600 }}>
              {initials(row.original.name)}
            </Avatar>
          )}
          <Typography sx={{ color: '#2c241e', fontWeight: 600, fontSize: '0.95rem', letterSpacing: 0.2 }}>
            {row.original.name}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'employee_id',
      header: 'User ID',
      Cell: ({ cell }) => (
        <Typography sx={{ color: '#3b3b3b', fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', letterSpacing: 0.3 }}>
          {cell.getValue() || '—'}
        </Typography>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      Cell: ({ cell, row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography sx={{ color: '#3b3b3b', fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', letterSpacing: 0.3 }}>
            {cell.getValue() || '—'}
          </Typography>
          {isNotOnboarded(row.original) && <EmailStatusChip status={emailStatusOf(row.original)} />}
        </Box>
      ),
    },
    {
      header: 'Role',
      accessorFn: (row) => resolveRole(row),
      Cell: ({ cell }) => {
        const role = cell.getValue();
        const color = ROLE_CHIP[ role ] || { bg: '#eeeeee', text: '#424242' };
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.4, py: 0.35, borderRadius: 10, bgcolor: color.bg, color: color.text, fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.3, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
            {role}
          </Box>
        );
      },
    },
    {
      header: 'Status',
      accessorKey: 'is_verified',
      size: 150,
      Cell: ({ cell }) => {
        const isVerified = cell.getValue();
        return (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.4, py: 0.4, borderRadius: 10, whiteSpace: 'nowrap',
            fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.4px',
            ...(isVerified
              ? { bgcolor: '#eafff0', color: '#1a7a3a', border: '1px solid #b6e8c4' }
              : { bgcolor: '#fff8e1', color: '#8a6d00', border: '1px dashed #d9b84a' }),
          }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: isVerified ? '#1a7a3a' : '#d9a400' }} />
            {isVerified ? 'Verified' : 'Not onboarded'}
          </Box>
        );
      },
    },

    // ── PLACEMENT COLUMN — students only ──
    ...(navigationIndex === 5 ? [ {
      header: 'Placement',
      id: 'placement',
      size: 180,
      Cell: ({ row }) => (
        <PlacementAccessCell
          user={row.original}
          onStatusChange={handlePlacementStatusChange}
          onNotOnboarded={(name) => showToast(`${name} hasn't signed up yet — cannot modify access`, 'warning')}
        />
      ),
    } ] : []),
  ];

  if (showActions) {
    const canUpdate = !!roleService.current?.canUpdateUser();
    const canDelete = !!roleService.current?.canDeleteUser();
    columns.push({
      id: 'actions',
      header: 'Actions',
      size: 176,
      enableSorting: false,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: 'center' },
      muiTableBodyCellProps: { align: 'center' },
      Cell: ({ row }) => {
        const user = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
            {showView && (
              <RowAction title="View details" onClick={() => handleViewClick(user)} icon={<VisibilityOutlinedIcon fontSize="small" />} />
            )}
            {canUpdate && (isNotOnboarded(user)
              ? <RowAction title="Edit email / ID, resend onboarding" tone="accent" onClick={() => handleContactClick(user)} icon={<MarkEmailReadOutlinedIcon fontSize="small" />} />
              : <EmptySlot />)}
            {showEdit && canUpdate && (
              <RowAction title="Edit role" onClick={() => handleEditClick(user)} icon={<EditOutlinedIcon fontSize="small" />} />
            )}
            {showDelete && canDelete && (
              <RowAction title="Delete" tone="danger" onClick={() => handleDeleteClick(user)} icon={<DeleteOutlineIcon fontSize="small" />} />
            )}
          </Box>
        );
      },
    });
  }

  // Role tabs (Admins / Sub admins / Super admin) get no total from the server
  const knownTotal = Number(totalEntities) || 0;

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 12, backgroundColor: '#fffef7', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ color: '#4d3e34', fontWeight: 800, letterSpacing: '-0.2px' }}>{heading}</Typography>
          <Typography variant="body2" sx={{ color: '#8a7f75', mt: 0.25 }}>Manage {heading.toLowerCase()} in your institute</Typography>
        </Box>
        <Chip
          label={searchState
            ? `${data.length}${searchState.truncated ? '+' : ''} result${data.length === 1 ? '' : 's'} for ${searchState.field} “${searchState.term}”`
            : (knownTotal > 0 ? `Showing ${data.length} of ${knownTotal} total ${heading.toLowerCase()}` : `Showing ${data.length} ${heading.toLowerCase()}`)}
          sx={{ bgcolor: '#fff8e1', color: '#4d3e34', fontWeight: 700, border: '1px solid rgba(77,62,52,0.15)' }}
        />
      </Box>

      {/* Search */}
      {(
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive size="small" value={searchField} aria-label="Search by"
            onChange={(_, value) => { if (!value) return; setSearchField(value); setError(''); if (searchInput.trim()) handleSearchClick(value); }}
            sx={{ '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(77,62,52,0.25) !important', borderRadius: '50px !important', px: 2, py: 0.6, textTransform: 'none', fontWeight: 700, color: '#4d3e34' }, gap: 0.5 }}
          >
            {SEARCH_FIELDS.map((f) => (
              <ToggleButton key={f.value} value={f.value} sx={{ '&.Mui-selected': { bgcolor: '#4d3e34 !important', color: '#ffde59 !important' } }}>{f.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            variant="outlined" size="small" placeholder={SEARCH_FIELDS.find((f) => f.value === searchField).placeholder}
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            error={!!error} helperText={error}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
            sx={{ width: '100%', maxWidth: 400, borderRadius: '50px', '& .MuiOutlinedInput-root': { borderRadius: '50px' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  {(searchState || searchInput) && (
                    <IconButton size="small" aria-label="Clear search" onClick={handleClearSearch}><ClearIcon fontSize="small" /></IconButton>
                  )}
                  <Button onClick={() => handleSearchClick()} variant="contained" size="small"
                    sx={{ borderRadius: '50px', padding: '6px 14px', backgroundColor: '#4d3e34', color: '#ffde59', '&:hover': { backgroundColor: '#3a2f27' } }}>
                    Go
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Table */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          rowCount={knownTotal}
          state={{ pagination, globalFilter }}
          onPaginationChange={handlePaginationChange}
          enableGlobalFilter={false}
          enableColumnFilters={true}
          enableColumnActions={false}
          enableStickyHeader
          enableTopToolbar={true}
          // Our own pager on every tab: the table's built-in one kept paging from a stale page number
          enablePagination={false}
          renderBottomToolbar={() => (
            <RangePager
              pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} rowsOnPage={data.length} total={knownTotal} hasNext={!dataFull}
              onPage={(index) => handlePaginationChange({ pageIndex: index, pageSize: pagination.pageSize })}
              onSize={(size) => handlePaginationChange({ pageIndex: 0, pageSize: size })}
            />
          )}
          {...(searchState ? {
            enablePagination: false,
            renderBottomToolbar: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1, bgcolor: '#fffdf5', borderTop: '1px solid rgba(77,62,52,0.08)' }}>
                <Typography variant="body2" sx={{ color: '#6b5a4b' }}>
                  {searchState.truncated ? `Showing the best matches only. Type more of the ${searchState.field} to narrow it down.` : `Matches start from the beginning of the ${searchState.field}.`}
                </Typography>
                <Button onClick={handleClearSearch} size="small" sx={{ textTransform: 'none', fontWeight: 700, color: '#4d3e34' }}>Back to full list</Button>
              </Box>
            ),
          } : {})}
          muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 3, border: '1px solid rgba(77,62,52,0.12)', overflow: 'hidden', bgcolor: '#ffffff' } }}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 380px)', minHeight: 240 } }}
          muiTopToolbarProps={{ sx: { bgcolor: '#fffdf5', borderBottom: '1px solid rgba(77,62,52,0.08)' } }}
          muiBottomToolbarProps={{ sx: { bgcolor: '#fffdf5', borderTop: '1px solid rgba(77,62,52,0.08)' } }}
          muiTableHeadCellProps={{ sx: { bgcolor: '#fbf6ea', color: '#4d3e34', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '2px solid rgba(77,62,52,0.15)', py: 1.4 } }}
          muiTableBodyRowProps={{ hover: false, sx: { '&:hover td': { backgroundColor: '#fff8e1' }, '& td': { transition: 'background-color .12s' } } }}
          muiTableBodyCellProps={{ sx: { borderBottom: '1px solid rgba(77,62,52,0.08)', py: 1 } }}
        />
      </Box>

      {/* ── CONTACT DIALOG (not-onboarded users only) ── */}
      <UserContactDialog
        open={contactDialog.open}
        user={contactDialog.user}
        onClose={() => setContactDialog({ open: false, user: null })}
        onUpdated={handleContactUpdated}
        showToast={showToast}
      />

      {/* ── VIEW DIALOG ── */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false })} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', backgroundColor: '#fffef7', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' } }}>
        <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg,#ffde59,#ffeb9a)', borderBottom: '3px solid #4d3e34', textAlign: 'center' }}>
          <Typography sx={{ color: '#4d3e34', fontWeight: 800, letterSpacing: 0.5 }}>UNILinkd • ID CARD</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          {viewDialog.user && (
            <Paper elevation={0} sx={{ p: 0, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', background: 'linear-gradient(180deg, rgba(255,222,89,0.2), rgba(255,255,255,0.6))', position: 'relative' }}>
              <Box sx={{ height: 6, width: '100%', bgcolor: '#4d3e34' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, pt: 2.5, pb: 2 }}>
                {viewDialog.user.profile_pic ? (
                  <Avatar src={viewDialog.user.profile_pic} sx={{ width: 84, height: 84, border: '3px solid #ffde59', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
                ) : (
                  <Avatar sx={{ width: 84, height: 84, backgroundColor: '#4d3e34', color: '#ffde59', fontSize: 30, fontWeight: 700, border: '3px solid #ffde59' }}>
                    {initials(viewDialog.user.name)}
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" sx={{ color: '#2c241e', fontWeight: 800, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {viewDialog.user.name || 'Unnamed User'}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', mt: 0.75, px: 1.2, py: 0.4, borderRadius: 999, border: '1px solid #4d3e34', bgcolor: '#ffffffa8' }}>
                    <Typography variant="caption" sx={{ color: '#4d3e34', fontWeight: 700, letterSpacing: 0.6 }}>
                      {resolveRole(viewDialog.user).toUpperCase()}
                    </Typography>
                  </Box>
                  {viewDialog.user.placementRevoked && (
                    <Box sx={{ display: 'inline-flex', mt: 0.5, ml: 0.5, px: 1.2, py: 0.4, borderRadius: 999, bgcolor: '#ffeaea', border: '1px solid #f5c6c6' }}>
                      <Typography variant="caption" sx={{ color: '#b82b2b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.6 }}>
                        PLACEMENT REVOKED
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />
              <Box sx={{ px: 3, py: 2 }}>
                {[
                  [ 'User ID', viewDialog.user?.employee_id || '—' ],
                  [ 'Name', viewDialog.user?.name || '—' ],
                  [ 'Role', resolveRole(viewDialog.user).toUpperCase() ],
                  [ 'Email', viewDialog.user?.email || '—' ],
                ].map(([ label, value ]) => (
                  <Grid container spacing={1.2} key={label} sx={{ mb: 1.5, backgroundColor: '#ffffffcc', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2, px: 1.5, py: 1 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#4d3e34', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" sx={{ color: '#3b3b3b', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>
                        {value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />
              <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fffef7' }}>
                <Typography variant="caption" sx={{ color: '#4d3e34', opacity: 0.8 }}>UNILinkd • Verified ID</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Box key={i} sx={{ width: i % 3 === 0 ? 3 : 2, height: 18, bgcolor: '#4d3e34', opacity: i % 4 === 0 ? 0.8 : 0.5 }} />
                  ))}
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button fullWidth onClick={() => setViewDialog({ open: false })} sx={{ color: '#4d3e34', fontWeight: 800, borderRadius: 2, border: '1px solid #4d3e34', '&:hover': { bgcolor: '#4d3e340a' } }}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false })} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#fffdf5' } }}>
        <DialogTitle sx={{ backgroundColor: '#ffde59', color: '#4d3e34', fontWeight: 700, textAlign: 'center' }}>
          Edit User Role
        </DialogTitle>
        <DialogContent dividers>
          {editDialog.user && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {editDialog.user.profile_pic ? (
                <Avatar src={editDialog.user.profile_pic} sx={{ width: 80, height: 80 }} />
              ) : (
                <Avatar sx={{ width: 80, height: 80, backgroundColor: '#4d3e34', color: '#ffde59', fontSize: 30, fontWeight: 600 }}>
                  {initials(editDialog.user.name)}
                </Avatar>
              )}
              <Typography variant="h6" color="#4d3e34">{editDialog.user.name}</Typography>
              {!isOnboarded(editDialog.user) && (
                <Typography variant="caption" sx={{ color: '#b82b2b', fontWeight: 600 }}>
                  ⚠️ User has not signed up yet
                </Typography>
              )}
              <TextField
                select fullWidth label="Select Role"
                value={editDialog.role}
                onChange={(e) => setEditDialog({ ...editDialog, role: e.target.value })}
                disabled={!isOnboarded(editDialog.user)}
                sx={{ mt: 2 }}
              >
                {ROLE_OPTIONS.map(role => <MenuItem key={role} value={role}>{role}</MenuItem>)}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRoleUpdate}
            disabled={!isOnboarded(editDialog.user)}
            sx={{ backgroundColor: '#4d3e34', color: '#ffde59', '&:hover': { backgroundColor: '#3a2f27' } }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>


      {/* ── ALUMNI DIALOG — students only ── */}
      <Dialog
        open={alumniDialog.open}
        onClose={() => setAlumniDialog({ open: false, user: null })}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#fffdf5' } }}
      >
        <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg,#ffde59,#ffeb9a)', borderBottom: '3px solid #4d3e34' }}>
          <Typography sx={{ color: '#4d3e34', fontWeight: 800, fontSize: 18 }}>
            Archive to Alumni Directory?
          </Typography>
          <Typography variant="caption" sx={{ color: '#4d3e34', opacity: 0.75 }}>
            {alumniDialog.user?.name} • {alumniDialog.user?.employee_id}
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#3b2d23', mb: 2.5 }}>
            Before deleting this student, you can save their record to the alumni directory so their profile, contact, and LinkedIn info are preserved.
          </Typography>

          {/* Toggle */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 2, borderRadius: 2, border: '1px solid #e6d5b8', bgcolor: '#fffaf0', mb: 3
          }}>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '0.95rem' }}>
                Add to Alumni Directory
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {alumniEnabled ? 'Student will be archived before deletion' : 'Student will be permanently deleted'}
              </Typography>
            </Box>
            <Box
              onClick={() => { setAlumniEnabled(v => !v); setAlumniConfirmText(''); }}
              sx={{
                width: 54, height: 30, borderRadius: 999,
                bgcolor: alumniEnabled ? '#ffcb77' : '#d1d5db',
                display: 'flex', alignItems: 'center', px: '4px',
                cursor: 'pointer', transition: '0.25s ease', boxSizing: 'border-box',
              }}
            >
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', bgcolor: '#fff',
                transform: alumniEnabled ? 'translateX(24px)' : 'translateX(0px)',
                transition: '0.25s ease', boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
              }} />
            </Box>
          </Box>

          {/* AWS-style confirmation when alumni is OFF */}
          {!alumniEnabled && (
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #f5c6c6', bgcolor: '#fff5f5' }}>
              <Typography variant="body2" sx={{ color: '#b82b2b', fontWeight: 700, mb: 0.5 }}>
                ⚠️ This student will NOT be archived
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b5a4b', display: 'block', mb: 1.5 }}>
                To confirm, type <b>DELETE</b> below to acknowledge that all data will be permanently lost.
              </Typography>
              <TextField
                fullWidth size="small"
                placeholder='Type "DELETE" to confirm'
                value={alumniConfirmText}
                onChange={(e) => setAlumniConfirmText(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: alumniConfirmText === 'DELETE' ? '#1a7a3a' : '#f5c6c6' },
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setAlumniDialog({ open: false, user: null })}
            variant="outlined"
            sx={{ borderColor: '#4d3e34', color: '#4d3e34', fontWeight: 700, borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAlumniDialogConfirm}
            variant="contained"
            disabled={!alumniEnabled && alumniConfirmText !== 'DELETE'}
            sx={{ bgcolor: '#4d3e34', color: '#ffde59', fontWeight: 800, borderRadius: 2, textTransform: 'none', px: 3, '&:hover': { bgcolor: '#3a2f27' }, '&:disabled': { opacity: 0.4 } }}
          >
            {alumniEnabled ? 'Archive & Delete' : 'Delete Without Archiving'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE DIALOG ── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#fffef7', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ffde59, #fff3a3)', color: '#4d3e34', fontWeight: 800, textAlign: 'center', fontSize: 20, letterSpacing: 0.4, pb: 1 }}>
          ⚠️ Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ color: '#3b2d23', mb: 1 }}>Are you sure you want to delete</Typography>
          <Typography variant="h6" sx={{ color: '#b82b2b', fontWeight: 800, mb: 1 }}>{deleteDialog.user?.name || 'this user'}</Typography>
          <Typography variant="body2" sx={{ color: '#6b5a4b', opacity: 0.9 }}>This action is <b>permanent</b> and cannot be undone.</Typography>
        </DialogContent>
        <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} />
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false })} variant="outlined" sx={{ borderColor: '#4d3e34', color: '#4d3e34', fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" sx={{ backgroundColor: '#b82b2b', color: '#fffef7', fontWeight: 800, borderRadius: 2, px: 3, textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TOAST WITH UNDO ── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.undoAction ? 5500 : 3000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setToast(t => ({ ...t, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            toast.undoAction ? (
              <Button size="small" startIcon={<UndoIcon fontSize="small" />} onClick={handleUndo}
                sx={{ color: 'inherit', fontWeight: 700, textTransform: 'none', ml: 1 }}>
                {toast.undoLabel}
              </Button>
            ) : null
          }
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </div>
  );
}

export default TableViewBox;